import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import MembershipEnquiryHero from '../components/sections/MembershipEnquiryHero'

const MembershipEnquiryPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Membership Enquiry | FGA Prestige - Exclusive Luxury Access</title>
        <meta name="description" content="Enquire about exclusive membership at FGA Prestige and unlock personalized luxury fashion services, priority access, and bespoke styling experiences." />
        <meta name="keywords" content="FGA Prestige membership enquiry, luxury fashion membership, exclusive fashion services, personal styling, bespoke shopping, VIP access" />
        <meta property="og:title" content="Membership Enquiry | FGA Prestige - Exclusive Luxury Access" />
        <meta property="og:description" content="Enquire about exclusive membership at FGA Prestige and unlock personalized luxury fashion services and VIP experiences." />
        <meta property="og:image" content="/images/membership-og.jpg" />
        <meta property="og:url" content="https://fgaprestige.com/membership-enquiry" />
      </Helmet>
      
      <main className="min-h-screen">
        <MembershipEnquiryHero />
        <ContactFormSection />
      </main>
    </>
  )
}

// Contact Form Section Component
function ContactFormSection() {
  return (
    <section className="min-h-screen bg-white py-16">
      <div className="max-w-4xl mx-auto px-8">
        <ContactForm />
      </div>
    </section>
  )
}

// Contact Form Component
function ContactForm() {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [error, setError] = React.useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    const formData = new FormData(e.currentTarget);
    const data = {
      salutation: formData.get('salutation'),
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      phoneCountry: formData.get('phoneCountry'),
      phoneNumber: formData.get('phoneNumber'),
      countryResidence: formData.get('countryResidence'),
      issueCategory: formData.get('issueCategory'),
      message: formData.get('message'),
      subscribe: !!formData.get('subscribe'),
      terms: !!formData.get('terms'),
    };

    try {
      const response = await fetch('/api/v1/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
          <span className="text-3xl text-white">✓</span>
        </div>
        <h2 className="text-2xl font-bold text-black mb-4">Thank You!</h2>
        <p className="text-gray-600 mb-8">
          Your enquiry has been sent successfully. We will get back to you within 2-3 business days.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}
      <form className="space-y-8" onSubmit={handleSubmit}>
        {/* Salutation */}
        <div>
          <label
            htmlFor="salutation"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              display: 'block',
              marginBottom: '8px'
            }}
          >
            Salutation
          </label>
          <select
            id="salutation"
            name="salutation"
            style={{
              width: '100%',
              padding: '0',
              border: 'none',
              borderBottom: '1px solid #000000',
              backgroundColor: 'transparent',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              outline: 'none',
              paddingBottom: '8px',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 8px center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
          >
            <option value="">Select salutation</option>
            <option value="Mr.">Mr.</option>
            <option value="Ms.">Ms.</option>
            <option value="Mrs.">Mrs.</option>
            <option value="Dr.">Dr.</option>
            <option value="Prof.">Prof.</option>
            <option value="Hon.">Hon.</option>
          </select>
        </div>

        {/* First Name and Last Name */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="firstName"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              First name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              required
              style={{
                width: '100%',
                padding: '0',
                border: 'none',
                borderBottom: '1px solid #000000',
                backgroundColor: 'transparent',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                outline: 'none',
                paddingBottom: '8px'
              }}
            />
          </div>
          
          <div>
            <label
              htmlFor="lastName"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              Last name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              required
              style={{
                width: '100%',
                padding: '0',
                border: 'none',
                borderBottom: '1px solid #000000',
                backgroundColor: 'transparent',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                outline: 'none',
                paddingBottom: '8px'
              }}
            />
          </div>
        </div>

        {/* Email Address */}
        <div>
          <label
            htmlFor="email"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              display: 'block',
              marginBottom: '8px'
            }}
          >
            Email address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            style={{
              width: '100%',
              padding: '0',
              border: 'none',
              borderBottom: '1px solid #000000',
              backgroundColor: 'transparent',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              outline: 'none',
              paddingBottom: '8px'
            }}
          />
        </div>

        {/* Phone Number with Country Code */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label
              htmlFor="phoneCountry"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              Country code
            </label>
            <select
              id="phoneCountry"
              name="phoneCountry"
              style={{
                width: '100%',
                padding: '0',
                border: 'none',
                borderBottom: '1px solid #000000',
                backgroundColor: 'transparent',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                outline: 'none',
                paddingBottom: '8px',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 8px center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '16px',
                paddingRight: '32px'
              }}
            >
              <option value="+852">+852</option>
              <option value="+1">+1</option>
              <option value="+44">+44</option>
              <option value="+86">+86</option>
              <option value="+81">+81</option>
              <option value="+82">+82</option>
              <option value="+65">+65</option>
              <option value="+60">+60</option>
              <option value="+66">+66</option>
              <option value="+84">+84</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label
              htmlFor="phoneNumber"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                display: 'block',
                marginBottom: '8px'
              }}
            >
              Phone number
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              style={{
                width: '100%',
                padding: '0',
                border: 'none',
                borderBottom: '1px solid #000000',
                backgroundColor: 'transparent',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                outline: 'none',
                paddingBottom: '8px'
              }}
            />
          </div>
        </div>

        {/* Country of Residence */}
        <div>
          <label
            htmlFor="countryResidence"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              display: 'block',
              marginBottom: '8px'
            }}
          >
            Country of residence*
          </label>
          <select
            id="countryResidence"
            name="countryResidence"
            required
            style={{
              width: '100%',
              padding: '0',
              border: 'none',
              borderBottom: '1px solid #000000',
              backgroundColor: 'transparent',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              outline: 'none',
              paddingBottom: '8px',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 8px center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
          >
            <option value="">Select country</option>
            <option value="Hong Kong">Hong Kong</option>
            <option value="China">China</option>
            <option value="United States">United States</option>
            <option value="United Kingdom">United Kingdom</option>
            <option value="Canada">Canada</option>
            <option value="Australia">Australia</option>
            <option value="Singapore">Singapore</option>
            <option value="Japan">Japan</option>
            <option value="South Korea">South Korea</option>
            <option value="Malaysia">Malaysia</option>
            <option value="Thailand">Thailand</option>
            <option value="Vietnam">Vietnam</option>
            <option value="Philippines">Philippines</option>
            <option value="Indonesia">Indonesia</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Issue Category */}
        <div>
          <label
            htmlFor="issueCategory"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              display: 'block',
              marginBottom: '8px'
            }}
          >
            Issue Category*
          </label>
          <select
            id="issueCategory"
            name="issueCategory"
            required
            style={{
              width: '100%',
              padding: '0',
              border: 'none',
              borderBottom: '1px solid #000000',
              backgroundColor: 'transparent',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              outline: 'none',
              paddingBottom: '8px',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 8px center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
          >
            <option value="">Select category</option>
            <option value="General Inquiry">General Inquiry</option>
            <option value="Membership Information">Membership Information</option>
            <option value="Personal Styling">Personal Styling</option>
            <option value="Bespoke Shopping">Bespoke Shopping</option>
            <option value="Member Services">Member Services</option>
            <option value="Editorial & Content">Editorial & Content</option>
            <option value="Technical Support">Technical Support</option>
            <option value="Feedback & Suggestions">Feedback & Suggestions</option>
            <option value="Partnership Opportunities">Partnership Opportunities</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Message Text Area */}
        <div>
          <label
            htmlFor="message"
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              display: 'block',
              marginBottom: '8px'
            }}
          >
            Let us know how we can help you... *
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            required
            style={{
              width: '100%',
              padding: '16px 0',
              border: '1px solid #000000',
              backgroundColor: 'transparent',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              color: '#000000',
              outline: 'none',
              resize: 'vertical',
              minHeight: '120px'
            }}
          />
        </div>

        {/* Checkboxes */}
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="subscribe"
              name="subscribe"
              value="subscribe"
              style={{
                marginTop: '4px',
                width: '16px',
                height: '16px',
                accentColor: '#000000'
              }}
            />
            <label
              htmlFor="subscribe"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                lineHeight: '1.5'
              }}
            >
              I would like to subscribe to FGA Prestige's Editorial
            </label>
          </div>
          
          <div className="flex items-start space-x-3">
            <input
              type="checkbox"
              id="terms"
              name="terms"
              value="terms"
              required
              style={{
                marginTop: '4px',
                width: '16px',
                height: '16px',
                accentColor: '#000000'
              }}
            />
            <label
              htmlFor="terms"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '16px',
                color: '#000000',
                lineHeight: '1.5'
              }}
            >
              I confirm that I have read and agree with the{' '}
              <Link
                to="/terms"
                style={{
                  textDecoration: 'underline',
                  textDecorationThickness: '1px',
                  textUnderlineOffset: '2px',
                  color: '#000000'
                }}
              >
                Terms & Conditions
              </Link>
              {' '}and{' '}
              <Link
                to="/privacy-policy"
                style={{
                  textDecoration: 'underline',
                  textDecorationThickness: '1px',
                  textUnderlineOffset: '2px',
                  color: '#000000'
                }}
              >
                Privacy Policy
              </Link>
              {' '}*
            </label>
          </div>
        </div>

        {/* Required Field Note */}
        <div style={{ marginTop: '24px' }}>
          <p
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '14px',
              color: '#000000',
              fontStyle: 'italic'
            }}
          >
            *Indicates required field
          </p>
        </div>

        {/* Submit Button */}
        <div style={{ marginTop: '32px' }}>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              backgroundColor: isSubmitting ? '#666666' : '#000000',
              color: '#FFFFFF',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '16px',
              fontWeight: '500',
              padding: '16px 32px',
              border: 'none',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default MembershipEnquiryPage